import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
// @ts-ignore
import { useContentToken, EditableToken, RichTextContent, useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import '../styles/careers-section-responsive.css';

const PHOTO_0_DEF: SlotGroupDefinition = {
  label: 'Team Photo 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const PHOTO_1_DEF: SlotGroupDefinition = {
  label: 'Team Photo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const PHOTO_2_DEF: SlotGroupDefinition = {
  label: 'Team Photo 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const PHOTO_3_DEF: SlotGroupDefinition = {
  label: 'Team Photo 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
};


export function CareersSection() {
  const label = useContentToken('reviews.label');
  const title = useContentToken('reviews.title');
  const ctaText = useContentToken('reviews.cta_text');
  const ctaUrl = useContentToken('reviews.cta_url');

  const photo0 = useSlotGroup('reviews.photo_0', PHOTO_0_DEF);
  const photo1 = useSlotGroup('reviews.photo_1', PHOTO_1_DEF);
  const photo2 = useSlotGroup('reviews.photo_2', PHOTO_2_DEF);
  const photo3 = useSlotGroup('reviews.photo_3', PHOTO_3_DEF);

  return (
    <section className="case-careers">
      <div className="case-careers-text">
        <EditableToken path="reviews.label" type="text" label="Section Label" defaultValue={contentSchema.tokens['reviews.label'].defaultValue} as="p" className="case-careers-label">
          {label}
        </EditableToken>
        <EditableToken path="reviews.title" type="text" label="Title" defaultValue={contentSchema.tokens['reviews.title'].defaultValue} as="h2" style={{ fontSize: 36, fontWeight: 700, marginBottom: '1rem', paddingTop: '0.75rem', textAlign: 'center' }}>
          {title}
        </EditableToken>
        <p>
          <RichTextContent
            path="reviews.body"
            label="Body Text"
            defaultValue={contentSchema.tokens['reviews.body'].defaultValue}
            as="span"
          />{' '}
            <EditableToken path="reviews.cta_url" type="url" label="CTA URL" defaultValue={contentSchema.tokens['reviews.cta_url'].defaultValue} as="span" style={{ display: 'contents' }}>
              <EditableToken path="reviews.cta_text" type="text" label="CTA Link Text" defaultValue={contentSchema.tokens['reviews.cta_text'].defaultValue} as="span" style={{ display: 'contents' }}>
              <a href={ctaUrl}>{ctaText}</a>
            </EditableToken>
          </EditableToken>
        </p>
      </div>
      <div className="case-careers-photos case-careers-photos-responsive">
        <EditableSlotGroup groupPath="reviews.photo_0" definition={PHOTO_0_DEF}>
          {photo0.image ? (
            <VibesitesImage url={photo0.image} alt={photo0.alt || ''} width={352} height={391} sizes="(min-width: 992px) 25vw, 50vw" />
          ) : (
            <div className="case-careers-photo-placeholder" aria-hidden />
          )}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="reviews.photo_1" definition={PHOTO_1_DEF}>
          {photo1.image ? (
            <VibesitesImage url={photo1.image} alt={photo1.alt || ''} width={352} height={391} sizes="(min-width: 992px) 25vw, 50vw" />
          ) : (
            <div className="case-careers-photo-placeholder" aria-hidden />
          )}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="reviews.photo_2" definition={PHOTO_2_DEF}>
          {photo2.image ? (
            <VibesitesImage url={photo2.image} alt={photo2.alt || ''} width={352} height={391} sizes="(min-width: 992px) 25vw, 50vw" />
          ) : (
            <div className="case-careers-photo-placeholder" aria-hidden />
          )}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="reviews.photo_3" definition={PHOTO_3_DEF}>
          {photo3.image ? (
            <VibesitesImage url={photo3.image} alt={photo3.alt || ''} width={352} height={391} sizes="(min-width: 992px) 25vw, 50vw" />
          ) : (
            <div className="case-careers-photo-placeholder" aria-hidden />
          )}
        </EditableSlotGroup>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Reviews',
  displayName: 'Careers Section',
  tokens: {
    'reviews.label': {
      label: 'Section Label',
      type: 'text',
      defaultValue: 'CAREERS',
    },
    'reviews.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Join the Case & Associates Team',
    },
    'reviews.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultValue: 'Ready for a life-changing career? Joining Case & Associates means being part of a team that cares about each other and loves working with people.',
    },
    'reviews.cta_text': {
      label: 'CTA Link Text',
      type: 'text',
      defaultValue: 'Careers page',
    },
    'reviews.cta_url': {
      label: 'CTA URL',
      type: 'url',
      defaultValue: '/careers/',
    },
  },
  slotGroups: {
    'reviews.photo_0': {
      label: 'Team Photo 1',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
    'reviews.photo_1': {
      label: 'Team Photo 2',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
    'reviews.photo_2': {
      label: 'Team Photo 3',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
    'reviews.photo_3': {
      label: 'Team Photo 4',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
  },
};

(CareersSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CareersSection;
